<template>
  <div v-if="target !== null" class="panel-switch">
    <tooltip :content="tooltipText" placement="right">
      <button
        type="button"
        class="panel-switch-button d-inline-flex align-items-center justify-content-center"
        :class="targetIsFlex === true ? 'panel-switch-button-flex' : 'panel-switch-button-agriculture'"
        :disabled="isSwitching === true"
        :aria-label="tooltipText"
        data-testid="retailer-panel-switch"
        @click="switchPanel"
      >
        <i class="mdi mdi-18px" :class="isSwitching === true ? 'mdi-loading mdi-spin' : 'mdi-swap-horizontal'"></i>
      </button>
    </tooltip>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useNotificationStore } from '@/stores/helpers/notification.js'
import { usePageLoadingStore } from '@/stores/helpers/pageLoading.js'
import { useAuthStore } from '@/stores/retailer/auth.js'
import { errorMessage } from '@/utilities/helpers.js'
import Retailer from '@/utilities/retailerHelper.js'
import Tooltip from '@/components/Tooltip.vue'

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const notificationStore = useNotificationStore()
const pageLoadingStore = usePageLoadingStore()
const { memberships } = storeToRefs(authStore)

const isSwitching = ref(false)

/**
 * The retailer this button moves to.
 *
 * Only a two panel toggle is offered for now, so a user with a third membership gets no button at
 * all: silently picking one of several would land them at a retailer they never chose. The list is
 * empty whenever the rollout has not reached the user, which hides the button the same way.
 */
const target = computed(() => {
  if (memberships.value.length !== 2) {
    return null
  }

  return memberships.value.find(membership => membership.is_active === false) ?? null
})

// The target, not the active retailer: two memberships are not necessarily on opposite sides, and
// in production most of them are not — 319 of the users holding exactly two are on the agriculture
// side twice, against 3 who straddle the two panels.
const targetIsFlex = computed(() => Retailer.isFlex(target.value))

const tooltipText = computed(() =>
  targetIsFlex.value === true ? t('views.layouts.switch_to_flex') : t('views.layouts.switch_to_agriculture')
)

/*
 * tr and en only. Tarfin Flex is a Turkey-only business, so a Romanian retailer never holds a second
 * membership and never sees this button; the ro locale is deliberately left without these strings.
 */
const switchingMessage = computed(() =>
  targetIsFlex.value === true ? t('views.layouts.switching_to_flex') : t('views.layouts.switching_to_agriculture')
)

// The loader wears the brand of the panel being opened, not the one being left — the same thing
// the button's own label does, and it means the animation already matches the panel that arrives.
const targetBrand = computed(() => (targetIsFlex.value === true ? 'flex' : 'agriculture'))

/**
 * Matches the fade in App.vue. The navigation waits for it: a veil frozen half way through would
 * jump to full white the moment the next document paints, which is the seam this avoids.
 */
const VEIL_FADE_MS = 250

/**
 * Reload the app rather than routing to the summary page.
 *
 * The sidebar, the route guards and every retailer store were filled from the retailer we are
 * leaving, and none of those stores can reset itself; booting the app again is the only way to
 * leave nothing of the old panel behind. Logout already makes the same move.
 *
 * The reload destroys whatever is animating, so the wait is covered in three steps. This side fades
 * the panel out to white with the loader on it and leaves both up: the browser holds this page on
 * screen through the navigation. index.html then repaints the same opaque white in the next
 * document's first frame, and App.vue puts the loader back once it has mounted. The loader is gone
 * for the few frames the bundle needs, but the ground under it never changes, so the documents swap
 * without a flash.
 */
const switchPanel = async () => {
  if (isSwitching.value === true || target.value === null) {
    return
  }

  isSwitching.value = true
  pageLoadingStore.startFullPage(switchingMessage.value, targetBrand.value)

  try {
    const veilHasFaded = new Promise(resolve => setTimeout(resolve, VEIL_FADE_MS))

    await Promise.all([authStore.switchRetailer(target.value.id), veilHasFaded])

    // Its own try: the switch has already happened server side by now, so a browser that refuses
    // storage must not send us down the failure path. The handover is decoration; losing it costs
    // the loader on the far side of the reload, nothing more.
    try {
      sessionStorage.setItem(
        'panelSwitch',
        JSON.stringify({ message: switchingMessage.value, brand: targetBrand.value })
      )
    } catch {
      // Private browsing, quota, storage disabled by policy — reload anyway.
    }

    window.location.assign(router.resolve({ name: 'retailer.dashboard' }).href)
  } catch (error) {
    isSwitching.value = false
    pageLoadingStore.stopFullPage()

    notificationStore.add({
      type: 'danger',
      message: errorMessage(error)
    })
  }
}
</script>

<style scoped>
.panel-switch-button {
  background-color: transparent;
  border-radius: 8px;
  border-style: solid;
  border-width: 1px;
  height: 32px;
  padding: 0;
  width: 32px;
}

.panel-switch-button-flex {
  border-color: var(--violet-700);
  color: var(--violet-700);
}

.panel-switch-button-agriculture {
  border-color: var(--primary);
  color: var(--primary);
}

body[data-leftbar-compact-mode='condensed'] .panel-switch {
  display: none;
}
</style>
